import apiClient from './base/apiClient'
import { MapMarker } from '../types/mapMarker'
import { MapFilterType } from '../constants/filter'
import { KakaoPlace } from './kakao'

export interface GetMarkersRequest {
  bounds: {
    north: number
    south: number
    east: number
    west: number
  }
  filter: MapFilterType
  /** PLAYLIST 필터일 때 선택한 플레이트리스트(BookmarkCategory) id */
  bookmarkCategoryId?: number | null
}

export interface GetMarkersResponse {
  markers: MapMarker[]
  count: number
}

export interface BusinessHoursItem {
  day: string
  openTime: string | null
  closeTime: string | null
  isRegularHoliday: boolean
}

export interface PlaceDetail {
  id: number
  name: string
  address: string | null
  phone_number: string | null
  category: 'restaurant' | 'cafe' | 'bar'
  /** 세부 카테고리 라벨 (예: '한식'). 카카오 category_name에서 파싱해 저장한 자유 문자열 */
  sub_category: string | null
  lat: number
  lng: number
  score: number | null // AB 테스트를 통해 산정된 최종 점수 (0~10)
  category_place_count: number // 동일 카테고리 등록 가게 수
  business_hours: BusinessHoursItem[]
  is_bookmarked: boolean
  bookmark_count: number // 북마크한 사용자 수
  has_user_review: boolean // 사용자 리뷰 작성 여부
  /** 이 장소에 내가 남긴 기록 수 (재방문). 2 이상일 때만 시트에 표시한다 */
  my_review_count?: number
  user_review_keywords: Array<{
    id: number
    name: string
  }> // 사용자 리뷰 키워드
  popular_review_keywords: Array<{
    id: number
    name: string
  }> // 전체 리뷰 기준 다빈도 키워드(최대 2개)
}

// K=3 존+순위 점수: 유저의 (카테고리, 존) 리뷰 장소를 순위(best→worst)로
export interface RankedZoneItem {
  placeId: number
  name: string
  address: string | null
  score: number
  /**
   * 비교하기 쉬우라고 붙여주는 '종류' (일식 · 한식 · 회 …).
   * 카카오 분류의 둘째 칸. 알 수 없으면 null.
   */
  kind?: string | null
}

export interface GetRankedZoneListRequest {
  placeId: number
  reviewCategoryId: number
  zone: number // 0|1|2|3
  excludePlaceId?: number // 리뷰 수정 시 자기 자신 제외
}

export interface RankedZoneListResponse {
  zone: number
  items: RankedZoneItem[]
  abRequired: boolean
}

/** 존 개수 (백엔드 ReviewScoreService::ZONE_COUNT와 일치) */
export const ZONE_COUNT = 4

/** 존 경계 (2/2/3/2). 백엔드 ReviewScoreService::ZONE_BOUNDS와 반드시 같아야 한다
 *  (다르면 존 정합 검사 scoreMatchesZone이 깨진다).
 *  존0 하한=1.0 → 최소 점수 1.0 (0.x 방지)
 *  2026-08-31: [1,2,5,8,10] → [1,3,5,8,10]. '아쉬워'가 2점대로 찍히던 문제. */
export const ZONE_BOUNDS = [1, 3, 5, 8, 10]

/** 점수(0~10) → 존 인덱스. 경계값은 위쪽 존에 포함 (2→1, 5→2, 8→3) */
export const zoneFromScore = (slider: number): number => {
  for (let z = ZONE_COUNT - 1; z >= 0; z--) {
    if (slider >= ZONE_BOUNDS[z]) return z
  }
  return 0
}

/** 존 인덱스(0~3) → 대표 점수(존 중앙). 버튼 선택값으로 사용 */
export const zoneToScore = (zone: number): number => {
  const z = Math.max(0, Math.min(ZONE_COUNT - 1, zone))
  return (ZONE_BOUNDS[z] + ZONE_BOUNDS[z + 1]) / 2 // 1.5 / 3.5 / 6.5 / 9
}

export interface SearchPlaceResponse {
  id: string | null
  name: string
  address: string
  phone_number: string
  business_hours: string
  /** 미저장 검색 결과만 (맵 프리뷰 쿼리용) */
  lat?: number | null
  lng?: number | null
  has_my_review: boolean
  is_bookmarked: boolean
  /** suggest의 미등록(카카오) 결과 — 탭 시 registerKakaoPlace로 등록용 카카오 원본 */
  kakao?: KakaoPlace
}

export interface SearchRegionResponse {
  kakao_id: string
  name: string
  category_name: string
  address: string
  lat: number
  lng: number
  type: 'subway'
}

export interface SearchPlacesResponse {
  places: SearchPlaceResponse[]
  regions: SearchRegionResponse[]
}


export interface GetRecommendedPlacesResponse {
  places: SearchPlaceResponse[]
}

export interface SuggestPlaceRequest {
  storeName: string
  region: string
}

export interface SuggestPlaceResponse {
  message: string
}

export interface PlaceCategoryIcon {
  code: string
  name: string
  icon_path: string | null
  marker_pin_path: string | null
  marker_pin_bookmarked_path: string | null
  category_icon_brown_path: string | null
}

export interface GetPlaceCategoriesResponse {
  categories: PlaceCategoryIcon[]
}

export interface MapSettingsResponse {
  cluster_dissolve_zoom: number
}

export const mapApi = {
  getMarkers: async (data: GetMarkersRequest): Promise<GetMarkersResponse> => {
    const response = await apiClient.post<GetMarkersResponse>(
      '/map/markers',
      {
        bounds: data.bounds,
        filter: data.filter || MapFilterType.ALL,
        ...(data.bookmarkCategoryId != null
          ? { bookmarkCategoryId: data.bookmarkCategoryId }
          : {}),
      }
    )
    return response.data
  },
  getPlace: async (id: string): Promise<PlaceDetail> => {
    const response = await apiClient.get<PlaceDetail>(`/map/place/${id}`)
    return response.data
  },
  // K=3 존+순위: 유저의 (카테고리, 존) 리뷰 장소를 순위로 받아 프론트에서 이진탐색
  getRankedZoneList: async (
    data: GetRankedZoneListRequest
  ): Promise<RankedZoneListResponse> => {
    try {
      const response = await apiClient.get<RankedZoneListResponse>(
        '/map/ranked-zone-list',
        { params: data }
      )
      return response.data
    } catch (error) {
      // 실패 시 빈 리스트로 폴백 (비교 없이 저장)
      return { zone: data.zone, items: [], abRequired: false }
    }
  },
  searchPlaces: async (
    keyword: string,
    location?: { lat: number; lng: number } | null
  ): Promise<SearchPlacesResponse> => {
    const response = await apiClient.get<SearchPlacesResponse>('/map/search', {
      params: {
        keyword,
        ...(location ? { lat: location.lat, lng: location.lng } : {}),
      },
    })
    return response.data
  },
  // 자동완성 제안 (타이핑 중, 부작용 없음: 카카오 1회 + DB우선, 자동등록 X)
  // location 전달 시 카카오 검색을 현재 위치 근접 보정(정확도 정렬 유지)
  suggestPlaces: async (
    keyword: string,
    signal?: AbortSignal,
    location?: { lat: number; lng: number } | null
  ): Promise<SearchPlacesResponse> => {
    const response = await apiClient.get<SearchPlacesResponse>('/map/suggest', {
      params: {
        keyword,
        ...(location ? { lat: location.lat, lng: location.lng } : {}),
      },
      signal,
    })
    return response.data
  },
  getRecommendedPlaces: async (): Promise<GetRecommendedPlacesResponse> => {
    const response = await apiClient.get<GetRecommendedPlacesResponse>(
      '/map/recommended-places'
    )
    return response.data
  },
  suggestPlace: async (
    data: SuggestPlaceRequest
  ): Promise<SuggestPlaceResponse> => {
    const response = await apiClient.post<SuggestPlaceResponse>(
      '/map/suggest-place',
      {
        storeName: data.storeName,
        region: data.region,
      }
    )
    return response.data
  },
  getPlaceCategories: async (): Promise<GetPlaceCategoriesResponse> => {
    const response = await apiClient.get<GetPlaceCategoriesResponse>(
      '/map/place-categories'
    )
    return response.data
  },
  getSettings: async (): Promise<MapSettingsResponse> => {
    const response = await apiClient.get<MapSettingsResponse>('/map/settings')
    return response.data
  },
}
